import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Crown,
  Sparkles,
  CheckCircle2,
  Check,
  Minus,
  Quote,
  Smartphone,
  ShieldCheck,
  Globe2,
  Building2,
  ChevronRight,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { publicApi } from "../lib/api";
import Header from "../components/Header";
import SubscribeModal from "../components/SubscribeModal";
import Footer from "../components/Footer";
import ChatFloatingButton from "../components/ChatFloatingButton";

export default function Pricing() {
  const { t, locale } = useI18n();
  const [plans, setPlans] = useState(null);
  const [billing, setBilling] = useState("annual");
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    publicApi.plans().then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);

  // SEO — title & meta description per locale
  useEffect(() => {
    const title =
      locale === "fr"
        ? "Tarifs Lexihab — Assistant juridique urbanisme Cameroun (à partir de 5 000 XAF/mois)"
        : "Lexihab Pricing — Cameroon Urban Law Legal Assistant (from 5,000 XAF/mo)";
    const desc =
      locale === "fr"
        ? "Comparez les plans Lexihab : gratuit 2 questions à vie, Standard 5 000 XAF/mois (réponses rapides), Pro 15 000 XAF/mois (analyses approfondies). Paiement MTN Mobile Money · Orange Money."
        : "Compare Lexihab plans: free 2 questions for life, Standard 5,000 XAF/month (fast answers), Pro 15,000 XAF/month (in-depth analysis). MTN Mobile Money · Orange Money payments.";
    document.title = title;
    setMeta("description", desc);
    setMeta("og:title", title, true);
    setMeta("og:description", desc, true);
  }, [locale]);

  const xaf = (n) => (n ? n.toLocaleString("fr-FR") + " XAF" : "—");
  const standardPrice = plans?.standard?.[billing];
  const proPrice = plans?.pro?.[billing];

  const annualSaving = useMemo(() => {
    if (!plans) return { standard: 0, pro: 0 };
    const s =
      plans.standard?.monthly && plans.standard?.annual
        ? Math.round(
            ((plans.standard.monthly * 12 - plans.standard.annual) /
              (plans.standard.monthly * 12)) *
              100
          )
        : 0;
    const p =
      plans.pro?.monthly && plans.pro?.annual
        ? Math.round(
            ((plans.pro.monthly * 12 - plans.pro.annual) /
              (plans.pro.monthly * 12)) *
              100
          )
        : 0;
    return { standard: s, pro: p };
  }, [plans]);

  const features = buildFeatureMatrix(locale);
  const testimonials = buildTestimonials(locale);

  return (
    <div className="min-h-screen" data-testid="pricing-page">
      <Header />

      {/* Hero */}
      <section className="relative bg-[#F8F9FA] border-b border-primary/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-10 text-center">
          <div className="inline-flex items-center gap-2 mb-5 px-3 py-1 border border-primary/20 bg-white rounded-sm">
            <Sparkles size={12} className="text-accent" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary font-semibold">
              {locale === "fr" ? "Tarifs · Mobile Money · XAF" : "Pricing · Mobile Money · XAF"}
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-primary leading-[1.05]">
            {locale === "fr"
              ? "Un droit précis, un prix transparent"
              : "Precise law, transparent pricing"}
          </h1>
          <p className="mt-5 text-base md:text-lg text-foreground/70 leading-relaxed max-w-2xl mx-auto">
            {locale === "fr"
              ? "Posez 2 questions gratuitement à vie, ou débloquez des réponses illimitées sourcées sur l'ensemble du corpus urbanisme camerounais."
              : "Ask 2 free questions for life, or unlock unlimited sourced answers across the entire Cameroonian urban-law corpus."}
          </p>

          {/* Billing toggle */}
          <div
            className="mt-8 inline-flex items-center gap-1 bg-white border border-primary/20 rounded-sm p-1"
            data-testid="pricing-billing-toggle"
          >
            <BillingPill
              active={billing === "monthly"}
              onClick={() => setBilling("monthly")}
              label={locale === "fr" ? "Mensuel" : "Monthly"}
              testid="pricing-billing-monthly"
            />
            <BillingPill
              active={billing === "annual"}
              onClick={() => setBilling("annual")}
              label={locale === "fr" ? "Annuel" : "Annual"}
              testid="pricing-billing-annual"
              badge={
                annualSaving.pro > 0
                  ? locale === "fr"
                    ? `-${annualSaving.pro}%`
                    : `-${annualSaving.pro}%`
                  : null
              }
            />
          </div>
        </div>
      </section>

      {/* Plan cards */}
      <section className="bg-white py-14 md:py-20 border-b border-primary/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-5">
          {/* Free */}
          <PlanCard
            tier="free"
            title={locale === "fr" ? "Découverte" : "Discover"}
            subtitle={
              locale === "fr"
                ? "Pour les citoyens curieux — tester sans engagement"
                : "For curious citizens — try without commitment"
            }
            price={locale === "fr" ? "Gratuit" : "Free"}
            priceSub={locale === "fr" ? "2 questions à vie" : "2 questions for life"}
            cta={locale === "fr" ? "Commencer" : "Get started"}
            to="/chat"
            perks={[
              locale === "fr" ? "Citations juridiques exactes" : "Exact legal citations",
              locale === "fr" ? "Corpus officiel camerounais" : "Official Cameroonian corpus",
              locale === "fr" ? "Réponses bilingues FR / EN" : "Bilingual FR / EN answers",
              locale === "fr" ? "Aucune carte bancaire requise" : "No credit card required",
            ]}
          />

          {/* Standard */}
          <PlanCard
            tier="standard"
            title="Standard"
            subtitle={
              locale === "fr"
                ? "Étudiants, chercheurs, citoyens avertis"
                : "Students, researchers, informed citizens"
            }
            price={xaf(standardPrice)}
            priceSub={billing === "monthly" ? (locale === "fr" ? "/mois" : "/month") : (locale === "fr" ? "/an" : "/year")}
            cta={locale === "fr" ? "S'abonner" : "Subscribe"}
            onClick={() => setModalOpen(true)}
            testid="pricing-cta-standard"
            savingBadge={
              billing === "annual" && annualSaving.standard > 0
                ? locale === "fr"
                  ? `Économisez ${annualSaving.standard}%`
                  : `Save ${annualSaving.standard}%`
                : null
            }
            perks={[
              locale === "fr" ? "Questions illimitées" : "Unlimited questions",
              locale === "fr" ? "Réponses rapides & citations sourcées" : "Fast answers & sourced citations",
              locale === "fr" ? "Historique des conversations" : "Conversation history",
              locale === "fr" ? "Paiement MTN / Orange Mobile Money" : "MTN / Orange Mobile Money",
            ]}
          />

          {/* Pro */}
          <PlanCard
            tier="pro"
            highlight
            title="Pro"
            subtitle={
              locale === "fr"
                ? "Notaires, avocats, promoteurs, architectes"
                : "Notaries, lawyers, developers, architects"
            }
            price={xaf(proPrice)}
            priceSub={billing === "monthly" ? (locale === "fr" ? "/mois" : "/month") : (locale === "fr" ? "/an" : "/year")}
            cta={locale === "fr" ? "Passer Pro" : "Go Pro"}
            onClick={() => setModalOpen(true)}
            testid="pricing-cta-pro"
            savingBadge={
              billing === "annual" && annualSaving.pro > 0
                ? locale === "fr"
                  ? `Économisez ${annualSaving.pro}%`
                  : `Save ${annualSaving.pro}%`
                : null
            }
            perks={[
              locale === "fr" ? "Tout le plan Standard" : "Everything in Standard",
              locale === "fr" ? "Analyses approfondies pour dossiers complexes" : "In-depth analysis for complex files",
              locale === "fr" ? "Raisonnement multi-textes & citations étendues" : "Multi-text reasoning & extended citations",
              locale === "fr" ? "Support prioritaire par e-mail" : "Priority email support",
            ]}
          />
        </div>
      </section>

      {/* Feature matrix */}
      <section className="bg-[#F8F9FA] py-14 md:py-20 border-b border-primary/10">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="mb-10">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent font-semibold mb-2">
              / 01 — {locale === "fr" ? "comparatif" : "comparison"}
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-primary tracking-tight">
              {locale === "fr"
                ? "Quel plan pour quel usage ?"
                : "Which plan for which use case?"}
            </h2>
          </div>

          <div
            className="bg-white border border-primary/15 rounded-sm overflow-x-auto"
            data-testid="pricing-feature-matrix"
          >
            <table className="w-full text-sm">
              <thead className="bg-primary text-white font-mono uppercase text-[11px] tracking-widest">
                <tr>
                  <th className="text-left px-4 md:px-6 py-4 font-semibold">
                    {locale === "fr" ? "Fonctionnalité" : "Feature"}
                  </th>
                  <th className="px-3 py-4 font-semibold">{locale === "fr" ? "Découverte" : "Discover"}</th>
                  <th className="px-3 py-4 font-semibold">Standard</th>
                  <th className="px-3 py-4 font-semibold bg-accent text-[#0D1B2A]">Pro</th>
                </tr>
              </thead>
              <tbody>
                {features.map((row, i) => (
                  <tr
                    key={i}
                    className={`${i % 2 === 0 ? "bg-white" : "bg-[#F8F9FA]/60"} border-t border-primary/10`}
                  >
                    <td className="px-4 md:px-6 py-3.5 text-foreground/80">{row.label}</td>
                    <Cell v={row.free} />
                    <Cell v={row.standard} />
                    <Cell v={row.pro} accent />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-5 text-xs text-foreground/50 font-mono">
            {locale === "fr"
              ? "Toutes les réponses sont sourcées sur le corpus juridique officiel. Aucun plan n'invente de référence."
              : "All answers are sourced from the official legal corpus. No plan invents a reference."}
          </p>
        </div>
      </section>

      {/* Payment methods */}
      <section className="bg-white py-14 border-b border-primary/10">
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-6">
          <InfoBlock
            icon={Smartphone}
            title={locale === "fr" ? "Mobile Money" : "Mobile Money"}
            desc={
              locale === "fr"
                ? "Paiement sécurisé via MTN Mobile Money ou Orange Money (Cameroun, XAF) — opéré par PawaPay."
                : "Secure payment via MTN Mobile Money or Orange Money (Cameroon, XAF) — operated by PawaPay."
            }
          />
          <InfoBlock
            icon={ShieldCheck}
            title={locale === "fr" ? "Sans carte bancaire" : "No credit card"}
            desc={
              locale === "fr"
                ? "Aucune carte requise. Vous recevez un jeton d'accès qui débloque les questions illimitées sur votre appareil."
                : "No card required. You receive an access token that unlocks unlimited questions on your device."
            }
          />
          <InfoBlock
            icon={Globe2}
            title={locale === "fr" ? "Bilingue" : "Bilingual"}
            desc={
              locale === "fr"
                ? "Interrogez en français ou en anglais — les sources officielles restent fidèles."
                : "Query in French or English — official sources remain faithful."
            }
          />
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-[#F8F9FA] py-14 md:py-20 border-b border-primary/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="mb-10 max-w-2xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent font-semibold mb-2">
              / 02 — {locale === "fr" ? "retours utilisateurs" : "user feedback"}
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-primary tracking-tight">
              {locale === "fr"
                ? "Pensé pour les professionnels et les citoyens"
                : "Built for professionals and citizens"}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5" data-testid="pricing-testimonials">
            {testimonials.map((t, i) => (
              <TestimonialCard key={i} {...t} />
            ))}
          </div>

          <p className="mt-6 text-[11px] text-foreground/40 font-mono uppercase tracking-wider">
            {locale === "fr"
              ? "* Profils illustratifs représentatifs des usages ciblés lors du pilote 2026."
              : "* Illustrative profiles representative of the targeted 2026 pilot use cases."}
          </p>
        </div>
      </section>

      {/* FAQ / close */}
      <section className="bg-white py-14 md:py-20 border-b border-primary/10">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="mb-10">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent font-semibold mb-2">
              / 03 — FAQ
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-primary tracking-tight">
              {locale === "fr" ? "Questions fréquentes" : "Frequently asked questions"}
            </h2>
          </div>
          <div className="divide-y divide-primary/10 border-y border-primary/10">
            {buildFAQ(locale).map((q, i) => (
              <FAQItem key={i} {...q} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-primary text-white py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
            {locale === "fr"
              ? "Prêt à sécuriser vos décisions juridiques ?"
              : "Ready to secure your legal decisions?"}
          </h2>
          <p className="mt-3 text-white/70 max-w-xl mx-auto">
            {locale === "fr"
              ? "Rejoignez les professionnels qui interrogent l'urbanisme camerounais en langage naturel — avec citations vérifiables."
              : "Join the professionals who query Cameroonian urban law in plain language — with verifiable citations."}
          </p>
          <div className="mt-7 inline-flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center justify-center gap-2 bg-accent text-[#0D1B2A] px-6 py-3.5 rounded-sm font-semibold text-sm hover:bg-accent-dark transition-colors"
              data-testid="pricing-final-cta-subscribe"
            >
              <Crown size={15} />
              {locale === "fr" ? "S'abonner maintenant" : "Subscribe now"}
            </button>
            <Link
              to="/chat"
              className="inline-flex items-center justify-center gap-2 border border-white/30 px-6 py-3.5 rounded-sm font-medium text-sm hover:bg-white/10 transition-colors"
              data-testid="pricing-final-cta-try"
            >
              {locale === "fr" ? "Tester gratuitement" : "Try for free"}
              <ChevronRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
      
      {/* Floating Chat Button */}
      <ChatFloatingButton />

      <SubscribeModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}

/* ---------- helpers & subcomponents ---------- */

function setMeta(name, content, og = false) {
  const key = og ? "property" : "name";
  let el = document.querySelector(`meta[${key}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(key, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function BillingPill({ active, onClick, label, testid, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`relative px-4 py-1.5 rounded-sm text-xs font-medium transition-all ${
        active ? "bg-primary text-white" : "text-foreground/70 hover:text-primary"
      }`}
    >
      {label}
      {badge && (
        <span className="ml-2 font-mono text-[10px] uppercase tracking-wider text-[#065F46] bg-[#D1FAE5] px-1.5 py-0.5 rounded-sm">
          {badge}
        </span>
      )}
    </button>
  );
}

function PlanCard({
  tier,
  title,
  subtitle,
  price,
  priceSub,
  perks,
  cta,
  onClick,
  to,
  highlight,
  savingBadge,
  testid,
}) {
  const Icon = tier === "pro" ? Crown : tier === "standard" ? Sparkles : Check;
  return (
    <div
      className={`relative flex flex-col bg-white border rounded-sm p-6 md:p-7 transition-all ${
        highlight
          ? "border-accent shadow-lg md:-translate-y-2"
          : "border-primary/15 hover:border-primary/30"
      }`}
      data-testid={testid || `pricing-plan-${tier}`}
    >
      {highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-[#0D1B2A] font-mono text-[10px] uppercase tracking-widest px-3 py-1 rounded-sm shadow-sm">
          {tier === "pro" ? "Plus populaire" : "Recommandé"}
        </div>
      )}

      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} className={tier === "pro" ? "text-accent-dark" : "text-primary"} />
        <h3 className="font-display text-xl font-semibold text-primary">{title}</h3>
      </div>
      <p className="text-xs text-foreground/60 mb-5 leading-relaxed">{subtitle}</p>

      <div className="mb-1 flex items-baseline gap-1">
        <span className="font-display text-4xl font-semibold text-primary">{price}</span>
        {priceSub && priceSub !== price && (
          <span className="font-mono text-[11px] uppercase tracking-widest text-foreground/50">
            {priceSub.startsWith("/") ? priceSub : ` · ${priceSub}`}
          </span>
        )}
      </div>
      {savingBadge ? (
        <span className="inline-block mt-1 font-mono text-[10px] uppercase tracking-wider text-[#065F46] bg-[#D1FAE5] px-2 py-0.5 rounded-sm self-start">
          {savingBadge}
        </span>
      ) : (
        <span className="block h-[18px]" aria-hidden />
      )}

      <ul className="mt-5 space-y-2.5 text-sm">
        {perks.map((p, i) => (
          <li key={i} className="flex items-start gap-2 text-foreground/80">
            <CheckCircle2
              size={14}
              className={`mt-0.5 shrink-0 ${tier === "pro" ? "text-accent-dark" : "text-primary"}`}
            />
            <span>{p}</span>
          </li>
        ))}
      </ul>

      <div className="mt-7 pt-4 border-t border-primary/10">
        {to ? (
          <Link
            to={to}
            className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-sm font-medium text-sm transition-colors ${
              tier === "pro"
                ? "bg-accent text-[#0D1B2A] hover:bg-accent-dark"
                : "bg-white text-primary border border-primary/30 hover:bg-primary/5"
            }`}
            data-testid={`pricing-cta-${tier}`}
          >
            {cta} <ChevronRight size={14} />
          </Link>
        ) : (
          <button
            type="button"
            onClick={onClick}
            className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-sm font-medium text-sm transition-colors ${
              tier === "pro"
                ? "bg-accent text-[#0D1B2A] hover:bg-accent-dark"
                : "bg-primary text-white hover:bg-primary-light"
            }`}
            data-testid={testid || `pricing-cta-${tier}`}
          >
            {cta} <ChevronRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function Cell({ v, accent }) {
  if (v === true) {
    return (
      <td className={`px-3 py-3.5 text-center ${accent ? "bg-accent/10" : ""}`}>
        <Check size={16} className={`inline ${accent ? "text-accent-dark" : "text-primary"}`} />
      </td>
    );
  }
  if (v === false) {
    return (
      <td className={`px-3 py-3.5 text-center ${accent ? "bg-accent/10" : ""}`}>
        <Minus size={14} className="inline text-foreground/30" />
      </td>
    );
  }
  return (
    <td className={`px-3 py-3.5 text-center text-[13px] text-foreground/80 ${accent ? "bg-accent/10 font-medium" : ""}`}>
      {v}
    </td>
  );
}

function InfoBlock({ icon: Icon, title, desc }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="w-10 h-10 bg-primary/10 text-primary rounded-sm flex items-center justify-center">
        <Icon size={18} strokeWidth={1.7} />
      </div>
      <div className="font-display text-lg font-semibold text-foreground">{title}</div>
      <p className="text-sm text-foreground/70 leading-relaxed">{desc}</p>
    </div>
  );
}

function TestimonialCard({ quote, role, context }) {
  return (
    <figure className="bg-white border border-primary/15 rounded-sm p-6 flex flex-col h-full">
      <Quote size={18} className="text-accent mb-3" />
      <blockquote className="text-[15px] text-foreground/85 leading-relaxed flex-1">
        “{quote}”
      </blockquote>
      <figcaption className="mt-5 pt-4 border-t border-primary/10">
        <div className="font-display text-sm font-semibold text-primary">{role}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-foreground/50 mt-0.5">
          {context}
        </div>
      </figcaption>
    </figure>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid="pricing-faq-item"
      >
        <span className="font-display text-base md:text-lg font-semibold text-primary">{q}</span>
        <ChevronRight
          size={16}
          className={`text-foreground/50 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>
      {open && <p className="mt-3 text-sm text-foreground/70 leading-relaxed">{a}</p>}
    </div>
  );
}

/* ---------- data ---------- */

function buildFeatureMatrix(locale) {
  const fr = [
    { label: "Questions par jour", free: "2", standard: "Illimité", pro: "Illimité" },
    { label: "Citations juridiques exactes", free: true, standard: true, pro: true },
    { label: "Corpus urbanisme 2004 → 2026", free: true, standard: true, pro: true },
    { label: "Bilingue FR / EN", free: true, standard: true, pro: true },
    { label: locale === "fr" ? "Profondeur d'analyse" : "Depth of analysis", free: locale === "fr" ? "Standard" : "Standard", standard: locale === "fr" ? "Rapide & précise" : "Fast & precise", pro: locale === "fr" ? "Approfondie, multi-textes" : "In-depth, multi-text" },
    { label: "Historique des conversations", free: false, standard: true, pro: true },
    { label: "Paiement Mobile Money", free: "—", standard: true, pro: true },
    { label: "Support prioritaire", free: false, standard: false, pro: true },
    { label: "Idéal pour dossiers complexes", free: false, standard: false, pro: true },
  ];
  const en = [
    { label: "Questions per day", free: "2", standard: "Unlimited", pro: "Unlimited" },
    { label: "Exact legal citations", free: true, standard: true, pro: true },
    { label: "Urban-law corpus 2004 → 2026", free: true, standard: true, pro: true },
    { label: "Bilingual FR / EN", free: true, standard: true, pro: true },
    { label: "Depth of analysis", free: "Standard", standard: "Fast & precise", pro: "In-depth, multi-text" },
    { label: "Conversation history", free: false, standard: true, pro: true },
    { label: "Mobile Money payment", free: "—", standard: true, pro: true },
    { label: "Priority support", free: false, standard: false, pro: true },
    { label: "Ideal for complex cases", free: false, standard: false, pro: true },
  ];
  return locale === "fr" ? fr : en;
}

function buildTestimonials(locale) {
  if (locale === "fr") {
    return [
      {
        quote:
          "Lexihab m'évite une heure par dossier — je cite directement l'article exact au lieu de ressortir la loi-cadre à chaque fois.",
        role: "Notaire, Yaoundé",
        context: "Dossiers fonciers & permis de construire",
      },
      {
        quote:
          "Les citations inline [1][2] sont vérifiables article par article. C'est le chaînon qui manquait entre les textes officiels et les décisions terrain.",
        role: "Chef de service · Administration",
        context: "Direction technique — Yaoundé",
      },
      {
        quote:
          "Les textes sont rarement accessibles au public ; Lexihab met les articles pertinents à portée d'un promoteur qui découvre un site.",
        role: "Promoteur immobilier, Douala",
        context: "Lotissements & PPP",
      },
    ];
  }
  return [
    {
      quote:
        "Lexihab saves me an hour per file — I cite the exact article instead of re-reading the framework law every time.",
      role: "Notary, Yaoundé",
      context: "Land files & building permits",
    },
    {
      quote:
        "Inline citations [1][2] are verifiable article by article. It's the missing link between the official texts and day-to-day decisions.",
      role: "Service Head · Public administration",
      context: "Technical Directorate — Yaoundé",
    },
    {
      quote:
        "Legal texts are rarely accessible to the public. Lexihab puts the relevant articles within reach of a developer discovering a new site.",
      role: "Real-estate developer, Douala",
      context: "Subdivisions & PPP projects",
    },
  ];
}

function buildFAQ(locale) {
  if (locale === "fr") {
    return [
      {
        q: "Comment fonctionne le paiement par Mobile Money ?",
        a: "Vous choisissez un plan, entrez votre e-mail, validez — nous ouvrons une session PawaPay sécurisée et vous réglez via MTN Mobile Money ou Orange Money (Cameroun, XAF). Un jeton d'accès est activé automatiquement dès la confirmation du paiement.",
      },
      {
        q: "Puis-je annuler mon abonnement ?",
        a: "Oui. Les abonnements mensuels sont sans engagement : à la date d'expiration, le jeton repasse en statut inactif et vous revenez au plan Découverte (2 questions à vie). Aucune reconduction automatique tant qu'un nouveau paiement n'est pas initié.",
      },
      {
        q: "Quelle différence entre Standard et Pro ?",
        a: "Standard répond vite avec des citations correctes — parfait pour des questions courantes (permis, lotissement, délais). Pro active des analyses approfondies : raisonnement sur plusieurs textes, citations étendues, nuances (expropriation, chaînons juridiques), support prioritaire. Idéal pour notaires, avocats et promoteurs sur des dossiers complexes.",
      },
      {
        q: "Mes conversations sont-elles confidentielles ?",
        a: "Les questions sont journalisées pour le suivi qualité interne (statistiques agrégées, taux de satisfaction). Les textes juridiques consultés sont publics. Nous ne partageons aucun identifiant avec des tiers publicitaires.",
      },
      {
        q: "Lexihab remplace-t-il un avocat ?",
        a: "Non. Lexihab fournit des réponses sourcées pour vous orienter ; aucune réponse ne constitue un conseil juridique officiel. Pour toute décision engageante, consultez un juriste inscrit au barreau du Cameroun.",
      },
    ];
  }
  return [
    {
      q: "How does Mobile Money payment work?",
      a: "Pick a plan, enter your email, confirm — we open a secure PawaPay session and you pay via MTN Mobile Money or Orange Money (Cameroon, XAF). An access token is activated automatically the moment the payment is confirmed.",
    },
    {
      q: "Can I cancel my subscription?",
      a: "Yes. Monthly plans require no commitment: on the expiry date the token becomes inactive and you return to the Discover plan (2 questions for life). There's no auto-renewal unless you initiate a new payment.",
    },
    {
      q: "What's the difference between Standard and Pro?",
      a: "Standard answers quickly with correct citations — great for common questions (permits, subdivisions, deadlines). Pro unlocks in-depth analysis: multi-text reasoning, extended citations, nuance (expropriation, legal chains) and priority support. Ideal for notaries, lawyers and developers on complex files.",
    },
    {
      q: "Are my conversations confidential?",
      a: "Questions are logged for internal quality monitoring (aggregate stats, satisfaction). The consulted legal texts are public. We share no identifier with ad networks.",
    },
    {
      q: "Does Lexihab replace a lawyer?",
      a: "No. Lexihab provides sourced answers to guide you; no answer constitutes official legal advice. For binding decisions consult a lawyer admitted to the Cameroonian Bar.",
    },
  ];
}
